import { useCallback, useEffect, useRef, useState } from 'react';
import { FeatureFlag, useFeatureToggle } from 'modules/unleash';
import { GeolocationPermissionState } from './types';

interface Options {
  isGeofenceCheckEnabled?: boolean;
  shouldCheckImmediately?: boolean;
  shouldWatch?: boolean;
}

export const useGeolocation = ({
  isGeofenceCheckEnabled = true,
  shouldCheckImmediately = false,
  shouldWatch = false
}: Options) => {
  const [permissionStatus, setPermissionStatus] = useState<
    GeolocationPermissionState | undefined
  >(undefined);
  const [coords, setCoords] = useState<GeolocationCoordinates | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const { isEnabled: isGeolocationEnabled } = useFeatureToggle(
    FeatureFlag.PRO_TALENT_IS_GEOLOCATION_ENABLED
  );

  const { isEnabled: isHighAccuracyEnabled } = useFeatureToggle(
    FeatureFlag.PRO_TALENT_IS_HIGH_ACCURACY_ENABLED
  );

  const isPending = permissionStatus === 'prompt';

  const watchIdRef = useRef<number | null>(null);

  const updatePermission = useCallback(() => {
    navigator.permissions
      .query({ name: 'geolocation' })
      .then(permissionStatus => {
        setPermissionStatus(permissionStatus.state);
      })
      .catch(error => {
        setPermissionStatus('unavailable');
        console.error('Error checking geolocation permissions:', error);
      });
  }, []);

  const getLocation = useCallback(() => {
    try {
      if (navigator.geolocation) {
        const geolocationOptions = {
          enableHighAccuracy: isHighAccuracyEnabled,
          maximumAge: 60000,
          timeout: 20000
        };

        setIsLoading(true);

        if (shouldWatch) {
          const watchId = navigator.geolocation.watchPosition(
            position => {
              setIsLoading(false);
              setCoords(position.coords);
              updatePermission();
            },
            error => {
              setIsLoading(false);
              setError(error.message);
              updatePermission();
            },
            geolocationOptions
          );
          watchIdRef.current = watchId;
        } else {
          navigator.geolocation.getCurrentPosition(
            position => {
              setIsLoading(false);
              setCoords(position.coords);
              updatePermission();
            },
            error => {
              setIsLoading(false);
              setError(error.message);
              updatePermission();
            },
            geolocationOptions
          );
        }
      } else {
        const errorInfo = 'Geolocation API not supported in this browser.';
        console.error(errorInfo);
        setError(errorInfo);
      }
    } catch (error) {
      console.error('Error querying geolocation:', error);
      setIsLoading(false);
      setPermissionStatus('denied');
    }
  }, [shouldWatch, isHighAccuracyEnabled, updatePermission]);

  const checkPermission = useCallback(async () => {
    try {
      const permissionStatus = await navigator.permissions.query({
        name: 'geolocation'
      });
      setPermissionStatus(permissionStatus.state);

      if (
        (shouldCheckImmediately && permissionStatus.state === 'prompt') ||
        permissionStatus.state === 'granted'
      ) {
        getLocation();
      }
    } catch (error) {
      setPermissionStatus('unavailable');
      console.error('Error checking permission:', error);
    }
  }, [getLocation, shouldCheckImmediately]);

  useEffect(() => {
    if (isGeofenceCheckEnabled && isGeolocationEnabled) {
      void checkPermission();
    }

    return () => {
      if (watchIdRef.current !== null && navigator.geolocation?.clearWatch) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
  }, [checkPermission, isGeofenceCheckEnabled, isGeolocationEnabled]);

  return {
    coords,
    error,
    isPending,
    permissionStatus,
    getLocation,
    isLoading
  };
};
